import React, { useEffect, useState } from 'react';
import Icon from './Icon';
import styles from './FolderTree.module.css';
import { getImpersonate, mailboxHref, switchMailbox } from '../lib/impersonate';

// The sidebar's Mailboxes list — only for people who were granted a shared
// mailbox (server: mail/mailboxAccess.js, GET /api/mail/mailboxes). It lists
// their own mailbox and each shared one (sales@) with its Inbox unread count,
// and the one this tab is working in is highlighted. A click switches this
// tab in place, Outlook-style — no page reload (lib/impersonate.js
// switchMailbox); each entry is a real link, so a middle-click or Ctrl+click
// still opens it in a tab of its own.
//
// Nobody else sees anything: with no shared mailboxes the list has one entry
// and this renders nothing.
//
// An admin viewing a colleague ("Open another mailbox" → Cara) sees CARA's
// list — the server is asked `?for=cara@…` — and opening Sales from there
// carries Cara along (owner=) so that tab still lists hers.
const POLL_MS = 60_000;

// Whose list this tab shows, when it is not the signed-in person's own: the
// colleague an admin is viewing, or the colleague a Sales tab was opened from.
export function listOwner(imp = getImpersonate()) {
  if (!imp) return null;
  if (imp.shared) return imp.owner ? { mailbox: imp.owner, displayName: imp.ownerName || imp.owner } : null;
  return { mailbox: imp.mailbox, displayName: imp.displayName || imp.mailbox };
}

export default function MailboxSwitcher({ pollMs = POLL_MS }) {
  const [entries, setEntries] = useState([]);
  const owner = listOwner();
  const ownerMailbox = owner?.mailbox || '';

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const url = '/api/mail/mailboxes' + (ownerMailbox ? `?for=${encodeURIComponent(ownerMailbox)}` : '');
        const r = await fetch(url, { credentials: 'same-origin' });
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled && Array.isArray(data?.mailboxes)) setEntries(data.mailboxes);
      } catch { /* keep the last list; it is only a convenience */ }
    }
    load();
    const t = setInterval(load, pollMs);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => { cancelled = true; clearInterval(t); window.removeEventListener('focus', onFocus); };
  }, [pollMs, ownerMailbox]);

  if (entries.length < 2) return null;
  const current = String(getImpersonate()?.mailbox || entries.find(e => e.own)?.mailbox || '').toLowerCase();

  return (
    <div className={styles.mailboxes} aria-label="Mailboxes">
      <div className={styles.mailboxesTitle}>Mailboxes</div>
      {entries.map(e => {
        const active = String(e.mailbox).toLowerCase() === current;
        return (
          <a
            key={e.mailbox}
            href={mailboxHref(e, undefined, owner)}
            className={`${styles.folder} ${styles.mailboxRow} ${active ? styles.folderActive : ''}`}
            aria-current={active ? 'true' : undefined}
            title={e.kind === 'shared' || (!e.kind && !e.own) ? `Shared mailbox — ${e.mailbox}` : e.kind === 'person' ? `${e.displayName}'s mailbox — ${e.mailbox}` : `Your mailbox — ${e.mailbox}`}
            onClick={ev => {
              // Modified / middle clicks keep the browser's own behaviour (new tab).
              if (ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
              ev.preventDefault();
              if (!active) switchMailbox(mailboxHref(e, undefined, owner));
            }}
          >
            <span className={styles.chevronSpace} />
            <span className={styles.icon}><Icon name={e.own ? 'person' : 'inbox'} size={14} /></span>
            <span className={styles.name}>
              {e.displayName || e.mailbox}
            </span>
            {e.unread > 0 && <span className={styles.unread}>{e.unread}</span>}
          </a>
        );
      })}
    </div>
  );
}
